import type { CalendarEventTypeKey } from './calendar-api.js';

/**
 * Přepočet období kalendáře na dotaz do API (krok CRM3).
 *
 * Modul je CISTÝ — žádatné React, žádatné síť. Důvod je stejný jako u
 * `task-board.ts`: hranice „dnes" musí být v kódu JEDNOU, jinak by
 * kalendář kreslil pondělí a API krájelo řádek do jiného dne.
 *
 * ROZHODNUTÍ CRM3-11 (hranice dne je poledník UTC, ne lokální čas
 * makléře): totéž jako CRM2-4. Server všechno vrací v UTC a `to`/`from`
 * musí být ve stejném světě. Makléř v Brně a server v UTC nejsou o hodinu
 * vedle sebe jen v zimě — a „kalendář na dnešek" by se v březnu posunul.
 */

export const CALENDAR_VIEWS = ['day', 'week', 'month', 'list'] as const;

/**
 * Převod zobrazení REOSu na view knihovny `@event-calendar/core`.
 * Je to CISTÁ mapovka, takže leží tady a ne v komponentě — widget jen
 * volá (jinak by se dvě místa mohly shodnout jen náhodou).
 */
const VIEW_MAP: Readonly<Record<string, string>> = {
  day: 'timeGridDay',
  week: 'timeGridWeek',
  month: 'dayGridMonth',
  list: 'listWeek',
};

export function calendarViewToLib(view: string): string {
  return VIEW_MAP[view] ?? 'timeGridWeek';
}

export type CalendarView = (typeof CALENDAR_VIEWS)[number];

export function isCalendarView(value: string): value is CalendarView {
  return (CALENDAR_VIEWS as readonly string[]).includes(value);
}

export const DAY_MS = 86_400_000;

export interface CalendarRange {
  /** Začátek okna včetně (ISO). */
  readonly from: string;
  /** Konec okna (ISO, výhradní). */
  readonly to: string;
  /** Titulek období („5. – 11. října 2026"). */
  readonly label: string;
}

/** Půlnoc UTC zadaného dne. */
export function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * Pondělí týdne, ve kterém `date` leží.
 *
 * `firstDay: 1` v Pobytníkově kalendáři znamená pondělí jako první den
 * týdne; český týden začíná v pondělí a REOS to tak má i v Dnešku.
 */
export function startOfUtcWeek(date: Date): Date {
  const day = startOfUtcDay(date);
  const weekday = (day.getUTCDay() + 6) % 7;
  return new Date(day.getTime() - weekday * DAY_MS);
}

export function startOfUtcMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function fmtRange(view: CalendarView, from: Date, to: Date, locale: string): string {
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions): string =>
    new Intl.DateTimeFormat(locale, { timeZone: 'UTC', ...opts }).format(d);
  // Měsíc je MĚSÍC, ne „1. 12. – 1. 1. 2027“ — to je čtecí past, v níž se
  // člověk musí podívat, které číslo je měsíc a které den.
  if (view === 'month') return fmt(from, { month: 'long', year: 'numeric' });
  if (view === 'day') return fmt(from, { day: 'numeric', month: 'long', year: 'numeric' });
  // Týden a seznam končí NEDĚLÍ, ale `to` je pondělí následujícího týdne
  // (výhradní hranice). Bez odečtení dne by nadpis vypadal jako „5. – 12.“.
  const lastDay = new Date(to.getTime() - DAY_MS);
  const sameMonth =
    from.getUTCMonth() === lastDay.getUTCMonth() && from.getUTCFullYear() === lastDay.getUTCFullYear();
  if (sameMonth) {
    // Krok KAL1: popisek je PODTITULEK hlavičky stránky („říjen 2026 · 5.–11.“),
    // ne řádek mezi tlačítky — oddělovač je tečka jako u ostatních obrazovek.
    return `${fmt(from, { month: 'long', year: 'numeric' })} · ${from.getUTCDate()}.–${lastDay.getUTCDate()}.`;
  }
  return `${fmt(from, { day: 'numeric', month: 'numeric' })} – ${fmt(lastDay, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  })}`;
}

/**
 * Okno pro zobrazení. Den a týden jsou pevné (den = 24 h, týden = 7 dní,
 * pondělí–neděle), měsíc bereme jako 1. až poslední den.
 */
export function calendarRange(
  view: CalendarView,
  anchor: Date,
  locale: string,
): CalendarRange {
  const from =
    view === 'day'
      ? startOfUtcDay(anchor)
      : view === 'week'
        ? startOfUtcWeek(anchor)
        : view === 'month'
          ? startOfUtcMonth(anchor)
          : startOfUtcWeek(anchor);
  const to =
    view === 'day'
      ? new Date(from.getTime() + DAY_MS)
      : view === 'week' || view === 'list'
        ? new Date(from.getTime() + 7 * DAY_MS)
        : new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1));
  return { from: from.toISOString(), to: to.toISOString(), label: fmtRange(view, from, to, locale) };
}

/** Posun o jeden krok ve zvoleném zobrazení. */
export function stepAnchor(view: CalendarView, anchor: Date, direction: 1 | -1): Date {
  if (view === 'day') return new Date(anchor.getTime() + direction * DAY_MS);
  if (view === 'week' || view === 'list') return new Date(anchor.getTime() + direction * 7 * DAY_MS);
  return new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + direction, 1));
}

/** Pondělí až neděle daného týdne (7 dnů, pořadí jako v kalendáři). */
export function weekDays(anchor: Date): readonly Date[] {
  const start = startOfUtcWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => new Date(start.getTime() + i * DAY_MS));
}

/*
 * ČAS V KALENDÁŘI SE KRESLÍ V PÁSMU MAKLÉŘE (krok KAL1).
 *
 * `@event-calendar/core` drží okamžik tak, že v UTC polích Date nese
 * MÍSTNÍ hodiny (`_fromLocalDate`). Dokud mu stránka posílala ISO řetězec
 * s „Z“, knihovna ho nepřečetla jako UTC — její `parseOffset` zná jen
 * `±HH:MM`, nikoli `Z` — a vykreslila 12:00 tam, kde je v Praze 14:00.
 * Seznam pod mřížkou přitom tutéž událost psal správně (`formatDateTime`),
 * takže jedna obrazovka tvrdila o jednom termínu dvě různé hodiny
 * (změřeno živě 6. 10. 2026: mřížka 12:00–13:00, seznam 14:00).
 *
 * Převod je tady, a ne v komponentě, protože ho potřebují OBA směry: dovnitř
 * knihovny i zpátky z přetažení myší. Kdyby každý směr počítal po svém,
 * posunutí události by ji po uložení přesunulo o posun pásma.
 *
 * Okno dotazu (`calendarRange`) zůstává v UTC podle ROZHODNUTÍ CRM3-11 —
 * to je hranice, podle které server VYBÍRÁ, ne podle které se kreslí.
 */

/** ISO okamžik -> `Date` pro knihovnu (místní hodiny v místních polích). */
export function toCalendarDate(iso: string): Date {
  return new Date(iso);
}

/** `Date` z knihovny (UTC pole = místní hodiny) -> ISO okamžik pro API. */
export function fromCalendarDate(date: Date): string {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  ).toISOString();
}

/**
 * Události, které se v okně mají ukázat.
 *
 * Klíčová věc: filtr je na Serveru, tady je jen pojistka pro případ, že by
 * se data v odpovědi rozutekla (např. dvě souběžné načtení s různým
 * oknem). Událost, která okno přesahuje, se v seznamu objeví dvakrát —
 * pokud by se v okně neobjevila vůbec, makléř by o prohlídce, která trval
 * přes půlnoc, nevěděl.
 */
export function visibleEvents<T extends { readonly startsAt: string; readonly endsAt: string }>(
  events: readonly T[],
  range: CalendarRange,
): readonly T[] {
  const from = new Date(range.from).getTime();
  const to = new Date(range.to).getTime();
  return events.filter((e) => {
    const start = new Date(e.startsAt).getTime();
    const end = new Date(e.endsAt).getTime();
    return start < to && end > from;
  });
}

/**
 * Legenda typů — barva a počet v okně.
 *
 * Pořadí je pořadí katalogu ze serveru, ne abecední: makléř zná
 * „prohlídka, podpis, předání" a ne „handover, meeting, task_due".
 */
export interface CalendarLegendEntry {
  readonly key: CalendarEventTypeKey;
  readonly label: string;
  readonly color: string;
  readonly textColor: string;
  readonly count: number;
}

export function calendarLegend<T extends { readonly eventType: CalendarEventTypeKey }>(
  types: readonly { readonly key: CalendarEventTypeKey; readonly label: string; readonly color: string; readonly textColor: string }[],
  events: readonly T[],
): readonly CalendarLegendEntry[] {
  return types.map((type) => ({
    key: type.key,
    label: type.label,
    color: type.color,
    textColor: type.textColor,
    count: events.filter((e) => e.eventType === type.key).length,
  }));
}

/**
 * Agenda po dnech (zobrazení Seznam, krok KAL1 bod 5).
 *
 * Den se počítá v pásmu makléře, ne v UTC: na telefonu je agenda jediné
 * zobrazení, které makléř v pondělí ráno uvidí, a „zítřejší" prohlídka
 * v 1:00 ráno nesmí skončit v dnešní skupině. Okno dotazu zůstává v UTC
 * (ROZHODNUTÍ CRM3-11) — tady se jen řadí to, co už přišlo.
 */
export interface CalendarDayGroup<T> {
  /** `YYYY-MM-DD` v pásmu makléře — klíč pro React, ne pro zobrazení. */
  readonly key: string;
  /** Nadpis skupiny („pondělí 5. října"). */
  readonly label: string;
  readonly items: readonly T[];
}

export function groupByLocalDay<T extends { readonly startsAt: string }>(
  events: readonly T[],
  locale: string,
): readonly CalendarDayGroup<T>[] {
  const groups = new Map<string, { label: string; items: T[] }>();
  const sorted = [...events].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const pad = (n: number): string => String(n).padStart(2, '0');
  for (const event of sorted) {
    const date = new Date(event.startsAt);
    if (Number.isNaN(date.getTime())) continue;
    const key = `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, {
        label: new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(date),
        items: [event],
      });
    } else {
      existing.items.push(event);
    }
  }
  return [...groups.entries()].map(([key, value]) => ({ key, label: value.label, items: value.items }));
}

/*
 * BARVA TYPU UDÁLOSTI JE TŘÍDA, NE HEX (krok KAL1).
 *
 * Do KAL1 brala karta barvu z odpovědi serveru (`event.color`) a stránka ji
 * dosazovala do `style`. Byly to pastely z jiné palety, které v tmavém režimu
 * svítily a žádný token je nedokázal přebít. Teď jde barva z tokenů
 * `--rh-ev-*` (`tokens-admin-v2.css`) a stránka posílá jen třídu.
 *
 * Hex ze serveru zůstává — potřebují ho CIZÍ kalendáře v odběru iCal
 * (`X-CRM-COLOR`), které naše tokeny přečíst neumějí. Je to tedy jedna
 * pravda o POŘADÍ A POJMENOVÁNÍ typů (server) a jedna o jejich VZHLEDU
 * V ADMINISTRACI (tokeny) — ne dvě kopie téhož.
 */
const TONE_CLASS: Readonly<Record<CalendarEventTypeKey, string>> = {
  viewing: 'rh-ec-ev--viewing',
  signing: 'rh-ec-ev--signing',
  handover: 'rh-ec-ev--handover',
  meeting: 'rh-ec-ev--meeting',
  task_due: 'rh-ec-ev--task',
};

/** Třída tónované plochy karty podle typu. Neznámý typ = neutrální tón. */
export function eventToneClass(eventType: string): string {
  return TONE_CLASS[eventType as CalendarEventTypeKey] ?? 'rh-ec-ev--other';
}
